import React from 'react'
// libs
import styled, { css } from 'styled-components'
// components
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIosRounded'
import MoreVertRounded from '@mui/icons-material/MoreVertRounded'
// themes
import { colors } from 'themes'
// utils
import { isDescendant } from 'utils/plan-tree'
import { size, transparentize } from 'polished'

// styled
const CollapseIcon = styled(({ isExpanded, hasDescendants, ...props }) => <ArrowForwardIcon {...props} />)`
  && {
    ${size(20)};
    cursor: pointer;
    color: ${colors.blue500};
    ${({ hasDescendants }) => !hasDescendants && `visibility: hidden`};
    ${({ isExpanded }) =>
      isExpanded &&
      css`
        transform: rotate(90deg);
      `}
  }
`

const DragIcon = styled(MoreVertRounded)`
  color: ${colors.gray900};
  cursor: move;
`

const ContentInnerWrapper = styled.div`
  position: relative;
  width: 100%;
`

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
`

const NodeContent = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`

const Wrapper = styled.div`
  display: flex;
  height: 100%;

  background: ${colors.gray200};
  border-radius: 5px;

  margin: 0 10px;
  padding: 5px 15px;

  &:hover {
    background: ${transparentize(0.8, colors.blue300)};
  }

  ${({ isLandingPad }) =>
    isLandingPad &&
    css`
      background: ${transparentize(0.7, colors.purple300)};
      border: 3px dotted ${colors.purple300};

      & > * {
        visibility: hidden;
      }
    `};
  ${({ isCancelPad }) =>
    isCancelPad &&
    css`
      background: ${transparentize(0.7, colors.red300)};
    `};
`

const NodeActions = styled.div`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);

  visibility: hidden;

  width: 150px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * {
    cursor: pointer;

    color: ${colors.gray300};
    text-decoration: underline;
    text-decoration-color: ${colors.gray300};

    &:hover {
      color: ${colors.gray900};
      text-decoration: underline;
      text-decoration-color: ${colors.gray900};
    }
  }
`

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  padding-left: 10px;
  ${({ canDrag }) => (canDrag ? 'cursor: move' : 'cursor: not-allowed;')};
  width: 100%;

  &:hover {
    ${NodeActions} {
      visibility: visible;
    }
  }
`

const AssigneeWrapper = styled.div`
  margin-left: 16px;
  font-size: 14px;
  color: ${colors.gray300};
`

const MinimalThemeNodeContentRenderer = (props) => {
  const {
    scaffoldBlockPxWidth,
    toggleChildrenVisibility,
    connectDragPreview,
    connectDragSource,
    isDragging,
    canDrop,
    canDrag,
    node,
    title,
    subtitle,
    draggedNode,
    path,
    treeIndex,
    isSearchMatch,
    isSearchFocus,
    icons,
    buttons,
    style,
    didDrop,
    swapFrom,
    swapLength,
    swapDepth,
    treeId, // Not needed, but preserved for other renderers
    isOver, // Not needed, but preserved for other renderers
    parentNode, // Needed for dndManager
    rowDirection,
    actionsHandlers,
    ...otherProps
  } = props

  const nodeTitle = title || node.title

  const isBeingDragged = draggedNode && isDescendant(draggedNode, node)
  const isLandingPadActive = !didDrop && isDragging

  const handleCollapseClick = () => toggleChildrenVisibility({ node, path, treeIndex })

  const hasDescendants = node.children && (!!node.children.length || typeof node.children === 'function')

  const connectDrag = (ref) => {
    if (canDrag) {
      connectDragSource(ref, { dropEffect: 'copy' })
      connectDragPreview(ref)
    }
  }

  const assignee =
    node.assignee && !node.isBusinessUnit && `Assigned to ${node.assignee.firstName} ${node.assignee.lastName}`

  return (
    <Wrapper
      {...otherProps}
      isLandingPad={isLandingPadActive}
      isCancelPad={isLandingPadActive && !canDrop}
      isBeingDragged={isBeingDragged}
      ref={connectDrag}>
      {toggleChildrenVisibility && (
        <IconsWrapper>
          {node.icon || (
            <CollapseIcon isExpanded={node.expanded} onClick={handleCollapseClick} hasDescendants={hasDescendants} />
          )}
          {canDrag && <DragIcon />}
        </IconsWrapper>
      )}

      <ContentWrapper canDrag={canDrag} className='node-content-wrapper'>
        <ContentInnerWrapper style={style}>
          <NodeContent>
            {typeof nodeTitle === 'function' ? nodeTitle({ node, path, treeIndex }) : nodeTitle}
            {node.assignee && <AssigneeWrapper>{assignee}</AssigneeWrapper>}
          </NodeContent>
          <NodeActions>
            <span onClick={(event) => actionsHandlers.handleEditClick({ event, node, path })}>edit</span>
            <span onClick={(event) => actionsHandlers.handleDeleteClick({ event, node, path })}>delete</span>
            <span onClick={(event) => actionsHandlers.handleAddChildClick({ event, node, path })}>add child</span>
          </NodeActions>
        </ContentInnerWrapper>
      </ContentWrapper>
    </Wrapper>
  )
}

MinimalThemeNodeContentRenderer.defaultProps = {
  buttons: [],
  canDrag: false,
  canDrop: false,
  className: '',
  draggedNode: null,
  icons: [],
  isSearchFocus: false,
  isSearchMatch: false,
  parentNode: null,
  style: {},
  subtitle: null,
  swapDepth: null,
  swapFrom: null,
  swapLength: null,
  title: null,
  toggleChildrenVisibility: null,
  rowDirection: 'ltr'
}

// MinimalThemeNodeContentRenderer.propTypes = {
//   actionsHandlers: shape({
//     handleEditClick: func,
//     handleDeleteClick: func,
//     handleAddChildClick: func
//   }),
//   buttons: arrayOf(node),
//   canDrag: bool,
//   className: string,
//   icons: arrayOf(node),
//   isSearchFocus: bool,
//   isSearchMatch: bool,
//   node: shape({}).isRequired,
//   path: arrayOf(oneOfType([string, number])).isRequired,
//   scaffoldBlockPxWidth: number.isRequired,
//   style: shape({}),
//   subtitle: oneOfType([func, node]),
//   swapDepth: number,
//   swapFrom: number,
//   swapLength: number,
//   title: oneOfType([func, node]),
//   toggleChildrenVisibility: func,
//   treeIndex: number.isRequired,
//   treeId: string.isRequired,
//
//   // Drag and drop API functions
//   // Drag source
//   connectDragPreview: func.isRequired,
//   connectDragSource: func.isRequired,
//   didDrop: bool.isRequired,
//   draggedNode: shape({}),
//   isDragging: bool.isRequired,
//   parentNode: shape({}), // Needed for dndManager
//   // Drop target
//   canDrop: bool,
//   isOver: bool.isRequired,
//   rowDirection: string
// }

export default MinimalThemeNodeContentRenderer
